/**
 * Step 2 "Configure Sizes" pieces for Apprise: the resolved VM profile shown
 * inside the size card, and the unverified-requirements notice.
 *
 * Three concepts are kept visibly separate: the Application SKU (the T-shirt
 * size the user picks), the Azure VM SKU (compute) and the Disk configuration.
 * The Small profile is never described as sufficient or supported for Apprise.
 */
import { AlertTriangle } from 'lucide-react'
import styles from '@/pages/NewDeploymentPage.module.css'
import type { VmSizing } from '@/types'
import { APPRISE_UNVERIFIED_NOTICE } from '@/utils/apprise'
import { IDENTITY_LABELS, osDiskLabel } from './appriseReview'

function Group({ title }: { title: string }) {
  return (
    <div style={{ marginTop: 6, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--aptean-text-muted)' }}>
      {title}
    </div>
  )
}

function Row({ label, value, tag }: { label: string; value: string; tag?: boolean }) {
  return (
    <div className={styles.sizeDetail}>
      <span>{label}</span>
      <span className={tag ? styles.skuTag : undefined}>{value}</span>
    </div>
  )
}

const onOff = (b: boolean) => (b ? 'On' : 'Off')

interface SizeDetailsProps {
  /** The size's VM profile (explicit or derived); null = the size has none. */
  vm: VmSizing | null
  sizeLabel: string
  /** The vCPU / RAM an administrator entered for the size (shown when there is no VM profile). */
  entered?: { cpu: string; memory: string }
  /** Users the size is meant for (shown when an administrator set it). */
  userCount?: number | null
}

export function AppriseSizeDetails({ vm, sizeLabel, entered, userCount }: SizeDetailsProps) {
  if (!vm) {
    return (
      <div className={styles.sizeDetails} data-testid="apprise-size-details">
        <Group title="Application SKU" />
        <Row label="Size" value={sizeLabel} />
        {userCount != null && <Row label="Users" value={String(userCount)} />}
        {entered && <Row label="Configured" value={`${entered.cpu} / ${entered.memory}`} />}
        <div className={styles.sizeDetail} data-testid="apprise-size-unavailable" style={{ color: 'var(--aptean-warning, #b26a00)' }}>
          No Azure VM size is set for this size and none matches its vCPU / RAM. Ask an administrator to set one
          (Admin Settings → Platform Config). It cannot be selected until then.
        </div>
      </div>
    )
  }
  return (
    <div className={styles.sizeDetails} data-testid="apprise-size-details">
      <Group title="Application SKU" />
      <Row label="Size" value={sizeLabel} />
      {userCount != null && <Row label="Users" value={String(userCount)} />}
      <Group title="Azure VM SKU" />
      <Row label="VM size" value={vm.vm_size} tag />
      {vm.derived && (
        <div className={styles.sizeDetail} data-testid="apprise-size-derived" style={{ fontSize: 11, color: 'var(--aptean-text-muted)' }}>
          Derived from the size's vCPU / RAM. You can pick another VM size on the next step.
        </div>
      )}
      <Row label="vCPU" value={String(vm.vcpu)} />
      <Row label="RAM" value={`${vm.memory_gb} GB`} />
      {vm.image && <Row label="Operating system" value={vm.image.label || `${vm.image.offer}:${vm.image.sku}`} />}
      <Group title="Disk configuration" />
      <Row label="OS disk" value={osDiskLabel(vm.os_disk_gb)} />
      <Row label="Disk type" value={vm.os_disk_type} tag />
      <Group title="Catalog defaults" />
      <Row label="Public IP" value={onOff(vm.public_ip_default)} />
      <Row label="Identity" value={IDENTITY_LABELS[vm.identity_default] ?? vm.identity_default} />
      <Row label="Monitoring" value={onOff(vm.monitoring_default)} />
      <Row label="Backup" value={onOff(vm.backup_default)} />
    </div>
  )
}

/** The unverified-requirements notice, shown wherever the Small profile is shown. */
export function AppriseUnverifiedNotice({ fallback }: { fallback?: boolean }) {
  return (
    <div className={styles.lbInfoBanner} role="note"
      style={{ borderColor: 'rgba(245, 166, 35, 0.5)', background: 'rgba(245, 166, 35, 0.1)' }}>
      <AlertTriangle size={13} style={{ display: 'inline', marginRight: 6, verticalAlign: '-2px' }} />
      {APPRISE_UNVERIFIED_NOTICE}
      {fallback && ' This size has no VM profile in the catalog yet — the standard Small defaults are used.'}
    </div>
  )
}
